import React, { useCallback, useEffect, useState } from 'react';
import {
    Button,
    Card,
    Descriptions,
    Drawer,
    Form,
    Input,
    InputNumber,
    Modal,
    Select,
    Space,
    Table,
    Tag,
    message,
} from 'antd';
import { LinkOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import PageHeader from '../../components/business/PageHeader';
import PermissionGuard from '../../components/business/PermissionGuard';
import RequestState from '../../components/business/RequestState';
import StatusTag from '../../components/business/StatusTag';
import { Company, CompanyEmployment, PageResult, Person } from '../../models/v2';
import { V2ApiError, v2Api } from '../../services/v2';
import { asV2Error, formatDate, formatDateTime } from '../../utils/v2';

const emptyPage: PageResult<Company> = { items: [], pageNo: 1, pageSize: 20, total: 0 };

const CompaniesPage = () => {
    const [form] = Form.useForm();
    const [employmentForm] = Form.useForm();
    const [data, setData] = useState(emptyPage);
    const [filters, setFilters] = useState<any>({ pageNo: 1, pageSize: 20 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<V2ApiError | null>(null);
    const [detail, setDetail] = useState<Company | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [createVisible, setCreateVisible] = useState(false);
    const [employmentVisible, setEmploymentVisible] = useState(false);
    const [linkablePersons, setLinkablePersons] = useState<Person[]>([]);
    const [personLoading, setPersonLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await v2Api.companies(filters));
        } catch (e) {
            setError(asV2Error(e));
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        const open = new URLSearchParams(window.location.hash.split('?')[1] || '').get('open');
        if (open) openDetail(Number(open));
    }, []);

    const openDetail = async (id: number) => {
        setDetailLoading(true);
        setDetail({ id } as Company);
        try {
            setDetail(await v2Api.company(id));
        } catch (e) {
            message.error(asV2Error(e).message);
            setDetail(null);
        } finally {
            setDetailLoading(false);
        }
    };

    const create = async () => {
        const values = await form.validateFields();
        if (!values.standardName && !values.shortName) {
            form.setFields([{ name: 'standardName', errors: ['企业全称或简称至少填写一项'] }]);
            return;
        }
        setSaving(true);
        try {
            const result = await v2Api.createCompany(values);
            message.success('企业已创建');
            setCreateVisible(false);
            form.resetFields();
            load();
            openDetail(result.id);
        } catch (e) {
            message.error(asV2Error(e).message);
        } finally {
            setSaving(false);
        }
    };

    const openEmploymentEditor = async () => {
        employmentForm.resetFields();
        employmentForm.setFieldsValue({
            startDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60_000)
                .toISOString()
                .slice(0, 10),
            reason: '建立企业联系人任职关系',
        });
        setEmploymentVisible(true);
        setPersonLoading(true);
        try {
            const result = await v2Api.persons({ pageNo: 1, pageSize: 100 });
            setLinkablePersons(
                (result.items || []).filter((person) => person.companyId !== detail?.id)
            );
        } catch (e) {
            message.error(`联系人加载失败：${asV2Error(e).message}`);
            setLinkablePersons([]);
        } finally {
            setPersonLoading(false);
        }
    };

    const linkEmployment = async () => {
        if (!detail) return;
        const values = await employmentForm.validateFields();
        setSaving(true);
        try {
            await v2Api.changeEmployment(Number(values.personId), {
                currentEmploymentId: values.currentEmploymentId
                    ? Number(values.currentEmploymentId)
                    : undefined,
                newCompanyId: detail.id,
                department: values.department,
                positionTitle: values.positionTitle,
                levelCode: values.levelCode,
                startDate: values.startDate,
                endDate: values.endDate,
                reason: values.reason,
            });
            message.success('联系人已关联到当前企业');
            setEmploymentVisible(false);
            employmentForm.resetFields();
            openDetail(detail.id);
        } catch (e) {
            message.error(asV2Error(e).message);
        } finally {
            setSaving(false);
        }
    };

    const contactColumns = [
        {
            title: '联系人',
            key: 'person',
            width: 140,
            render: (_: any, row: CompanyEmployment) => (
                <Button
                    type="link"
                    className="v2-link-strong"
                    style={{ padding: 0 }}
                    onClick={() => {
                        window.location.hash = `#/app/v2/customer/persons?open=${row.personId}`;
                    }}
                >
                    {row.preferredName || row.name || `联系人 #${row.personId}`}
                </Button>
            ),
        },
        {
            title: '部门 / 职位',
            key: 'position',
            render: (_: any, row: CompanyEmployment) =>
                [row.department, row.positionTitle].filter(Boolean).join(' · ') || '待完善',
        },
        {
            title: '联系方式',
            key: 'contact',
            width: 160,
            render: (_: any, row: CompanyEmployment) =>
                row.maskedContact ? (
                    <span>
                        {row.maskedContact}
                        {row.contactMethodType ? `（${row.contactMethodType}）` : ''}
                    </span>
                ) : (
                    <span className="v2-muted">待完善</span>
                ),
        },
        {
            title: '任职状态',
            dataIndex: 'employmentStatus',
            width: 100,
            render: (value: string) => <StatusTag value={value} />,
        },
        {
            title: '任职时间',
            key: 'period',
            width: 170,
            render: (_: any, row: CompanyEmployment) =>
                `${row.startDate ? formatDate(row.startDate) : '未填写'} → ${row.endDate ? formatDate(row.endDate) : '至今'}`,
        },
    ];

    const columns = [
        {
            title: '企业名称',
            key: 'name',
            width: 220,
            render: (_: any, row: Company) => (
                <Button className="v2-link-strong" type="link" onClick={() => openDetail(row.id)}>
                    {row.shortName || row.standardName || `企业 #${row.id}`}
                </Button>
            ),
        },
        {
            title: '行业 / 地区',
            key: 'industry',
            width: 180,
            render: (_: any, row: Company) => (
                <div>
                    {row.industry || '待完善'}
                    <div className="v2-muted">
                        {[row.province, row.city].filter(Boolean).join(' · ') || '地区待完善'}
                    </div>
                </div>
            ),
        },
        {
            title: '负责人',
            dataIndex: 'ownerName',
            width: 110,
            render: (v: string, row: Company) =>
                v || row.accountOwnerName || row.accountOwnerId || '待分配',
        },
        {
            title: '优先级',
            dataIndex: 'priorityCode',
            width: 90,
            render: (v: string) => <StatusTag value={v} />,
        },
        {
            title: '客户关系',
            dataIndex: 'relationshipType',
            width: 120,
            render: (v: string) => v || '—',
        },
        {
            title: '经营信号',
            key: 'signals',
            width: 200,
            render: (_: any, row: Company) => (
                <Space size={4} className="v2-wrap-space">
                    {row.doNotContact ? (
                        <Tag color="red">禁止触达</Tag>
                    ) : (
                        <span className="v2-muted">详情查看经营关联</span>
                    )}
                </Space>
            ),
        },
        { title: '最近更新', dataIndex: 'lastEditTime', width: 170, render: formatDateTime },
        {
            title: '状态',
            dataIndex: 'masterStatus',
            width: 90,
            render: (v: string) => <StatusTag value={v} />,
        },
    ];

    return (
        <div className="v2-page">
            <PageHeader
                eyebrow="客户 · 企业账户"
                title="企业客户"
                description="企业是长期经营主对象；经营信号由沟通、商机、项目和触达限制实时派生。"
                extra={
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={load}>
                            刷新
                        </Button>
                        <PermissionGuard anyOf={['CUSTOMER_COMPANY_EDIT']}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setCreateVisible(true)}
                            >
                                快速建档
                            </Button>
                        </PermissionGuard>
                    </Space>
                }
            />
            <Card className="v2-filter-card">
                <Space className="v2-wrap-space">
                    <Input.Search
                        allowClear
                        prefix={<SearchOutlined />}
                        placeholder="企业名称"
                        style={{ width: 280 }}
                        onSearch={(keyword) => setFilters({ ...filters, keyword, pageNo: 1 })}
                    />
                    <Select
                        allowClear
                        placeholder="主档状态"
                        style={{ width: 150 }}
                        onChange={(status) => setFilters({ ...filters, status, pageNo: 1 })}
                    >
                        <Select.Option value="ACTIVE">有效</Select.Option>
                        <Select.Option value="MERGED">已合并</Select.Option>
                    </Select>
                    <InputNumber
                        placeholder="负责人 ID"
                        min={1}
                        onChange={(ownerId) => setFilters({ ...filters, ownerId, pageNo: 1 })}
                    />
                </Space>
            </Card>
            <Card className="v2-panel" bordered={false}>
                <RequestState
                    loading={loading}
                    error={error}
                    empty={!data.items.length}
                    onRetry={load}
                >
                    <Table
                        rowKey="id"
                        columns={columns}
                        dataSource={data.items}
                        scroll={{ x: 1120 }}
                        pagination={{
                            current: data.pageNo,
                            pageSize: data.pageSize,
                            total: data.total,
                            showSizeChanger: true,
                            showTotal: (total) => `共 ${total} 家企业`,
                            onChange: (pageNo, pageSize) =>
                                setFilters({ ...filters, pageNo, pageSize }),
                        }}
                    />
                </RequestState>
            </Card>
            <Drawer
                width={720}
                title={detail && (detail.shortName || detail.standardName || `企业 #${detail.id}`)}
                visible={!!detail}
                onClose={() => setDetail(null)}
                destroyOnClose
            >
                <RequestState
                    loading={detailLoading}
                    empty={false}
                    onRetry={() => detail && openDetail(detail.id)}
                >
                    {detail && (
                        <>
                            <div className="v2-detail-hero">
                                <Space className="v2-wrap-space">
                                    <StatusTag value={detail.masterStatus} />
                                    <StatusTag value={detail.priorityCode} />
                                    {detail.hasRestriction && <Tag color="red">存在触达限制</Tag>}
                                </Space>
                                <p>{detail.remark || '尚未填写企业备注'}</p>
                            </div>
                            <Descriptions bordered size="small" column={2} title="身份与分类信息">
                                <Descriptions.Item label="标准全称" span={2}>
                                    {detail.standardName || '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label="英文名">
                                    {detail.englishName || detail.ename || '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label="行业">
                                    {detail.industry || '待完善'}
                                </Descriptions.Item>
                                <Descriptions.Item label="国家 / 地区">
                                    {[detail.country, detail.province, detail.city]
                                        .filter(Boolean)
                                        .join(' · ') || '待完善'}
                                </Descriptions.Item>
                                <Descriptions.Item label="规模">
                                    {detail.sizeCode || '待完善'}
                                </Descriptions.Item>
                                <Descriptions.Item label="负责人">
                                    {detail.ownerName ||
                                        detail.accountOwnerName ||
                                        detail.accountOwnerId ||
                                        '待分配'}
                                </Descriptions.Item>
                                <Descriptions.Item label="关系类型">
                                    {detail.relationshipType || '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label="版本">
                                    v{detail.version || 0}
                                </Descriptions.Item>
                                <Descriptions.Item label="更新时间">
                                    {formatDateTime(detail.updatedAt || detail.lastEditTime)}
                                </Descriptions.Item>
                            </Descriptions>
                            <Card
                                className="v2-section"
                                size="small"
                                title={`联系人（${detail.employments?.length || 0}）`}
                                extra={
                                    <Button icon={<LinkOutlined />} onClick={openEmploymentEditor}>
                                        关联已有联系人
                                    </Button>
                                }
                            >
                                <Table
                                    rowKey="id"
                                    size="small"
                                    pagination={false}
                                    columns={contactColumns}
                                    dataSource={detail.employments || []}
                                    scroll={{ x: 760 }}
                                    locale={{
                                        emptyText: '暂无关联联系人，可点击右上角建立任职关系',
                                    }}
                                />
                            </Card>
                        </>
                    )}
                </RequestState>
            </Drawer>
            <Modal
                title="企业快速建档"
                visible={createVisible}
                onOk={create}
                confirmLoading={saving}
                onCancel={() => setCreateVisible(false)}
                okText="创建企业"
                cancelText="取消"
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        priorityCode: 'NORMAL',
                        relationshipType: 'PROSPECT',
                        country: '中国',
                        businessTimezone: 'Asia/Shanghai',
                    }}
                >
                    <Form.Item label="企业标准全称" name="standardName">
                        <Input maxLength={200} placeholder="全称与简称至少填写一项" />
                    </Form.Item>
                    <Form.Item label="企业简称" name="shortName">
                        <Input maxLength={100} />
                    </Form.Item>
                    <Form.Item
                        label="负责人 ID"
                        name="accountOwnerId"
                        rules={[{ required: true, message: '请选择负责人' }]}
                    >
                        <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Space size="large" style={{ display: 'flex' }}>
                        <Form.Item
                            label="优先级"
                            name="priorityCode"
                            rules={[{ required: true }]}
                            style={{ flex: 1 }}
                        >
                            <Select>
                                <Select.Option value="HIGH">高</Select.Option>
                                <Select.Option value="MEDIUM">中</Select.Option>
                                <Select.Option value="NORMAL">普通</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item
                            label="关系类型"
                            name="relationshipType"
                            rules={[{ required: true }]}
                            style={{ flex: 1 }}
                        >
                            <Select>
                                <Select.Option value="PROSPECT">潜在客户</Select.Option>
                                <Select.Option value="CUSTOMER">现有客户</Select.Option>
                                <Select.Option value="FORMER_CUSTOMER">历史客户</Select.Option>
                            </Select>
                        </Form.Item>
                    </Space>
                    <Form.Item label="行业" name="industry">
                        <Input />
                    </Form.Item>
                    <Form.Item label="备注" name="remark">
                        <Input.TextArea rows={3} maxLength={1000} />
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                title={`关联联系人${detail ? ` · ${detail.shortName || detail.standardName}` : ''}`}
                visible={employmentVisible}
                zIndex={1200}
                onOk={linkEmployment}
                confirmLoading={saving}
                onCancel={() => setEmploymentVisible(false)}
                okText="确认关联"
                destroyOnClose
            >
                <Form form={employmentForm} layout="vertical">
                    <Form.Item
                        label="选择联系人"
                        name="personId"
                        rules={[{ required: true, message: '请选择需要关联的联系人' }]}
                        extra="列表会排除已经关联当前企业的联系人；输入姓名、企业或 ID 可筛选。"
                    >
                        <Select
                            showSearch
                            loading={personLoading}
                            optionFilterProp="label"
                            placeholder="搜索并选择联系人"
                            options={linkablePersons.map((person) => ({
                                value: person.id,
                                label: `${person.preferredName || person.name}（ID: ${person.id}${
                                    person.companyName ? `，当前：${person.companyName}` : ''
                                }）`,
                            }))}
                            onChange={(personId) => {
                                const person = linkablePersons.find((item) => item.id === personId);
                                employmentForm.setFieldsValue({
                                    currentEmploymentId: person?.employmentId,
                                });
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        label="原任职关系 ID"
                        name="currentEmploymentId"
                        extra="联系人当前在其他企业任职时会自动带出；系统将结束原关系后再转入当前企业。"
                    >
                        <InputNumber min={1} precision={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Space align="start" style={{ display: 'flex' }}>
                        <Form.Item label="部门" name="department">
                            <Input maxLength={100} />
                        </Form.Item>
                        <Form.Item label="职位" name="positionTitle">
                            <Input maxLength={100} />
                        </Form.Item>
                        <Form.Item label="职级" name="levelCode">
                            <Input maxLength={30} />
                        </Form.Item>
                    </Space>
                    <Form.Item label="入职 / 关系开始日期" name="startDate">
                        <Input type="date" />
                    </Form.Item>
                    <Form.Item
                        label="关联原因"
                        name="reason"
                        rules={[{ required: true, whitespace: true, message: '请填写关联原因' }]}
                    >
                        <Input.TextArea rows={2} maxLength={500} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default CompaniesPage;
